import type { Scope } from '@ferrari/types';

/**
 * Arquitetura de informacao do Console.
 *
 * Fonte: o layout entregue pelo cliente em 17/09/2026,
 * `Ferrari_Instagram_AI_Console_Developer_Layout_Package_v1.0`, na barra
 * lateral de `reference/console_visual_reference.png`. Sao 16 areas, mais
 * "Ajuda e Documentacao" ancorada no rodape.
 *
 * ## Por que o PNG, e nao um dos outros tres desenhos do mesmo pacote
 *
 * O pacote traz a navegacao em tres versoes que NAO batem entre si, e a SPEC
 * v1.5 §12 traz uma quarta:
 *
 * | onde                                | areas |
 * | ----------------------------------- | ----- |
 * | Console SPEC v1.5 §12               | 18    |
 * | reference/console_visual_reference  | 16    |
 * | docs/UI_SCREEN_MAP.md               | 12    |
 * | mockup/index.html (barra lateral)   | 11    |
 *
 * O Authority Map v1.0 §3 manda nao escolher em silencio quando dois documentos
 * vigentes conflitam. Entao a escolha foi MEDIDA, e nao arbitrada: as 11 do
 * mockup sao um subconjunto ESTRITO das 16 do PNG, e as 12 do screen map sao as
 * mesmas 11 mais "Midia Paga", que o PNG tambem tem. Ou seja, os dois desenhos
 * menores sao vistas PARCIAIS do mesmo desenho maior, e nao alternativas a ele:
 * o mockup simplesmente nao prototipou todas as telas. O PNG e o unico que
 * contem os outros dois, e por isso e "o layout como foi entregue".
 *
 * A lista de 18 da SPEC §12 continua divergente e foi registrada como pergunta
 * ao cliente, nao resolvida aqui.
 *
 * ## O que NAO mudou, e por que
 *
 * Os CAMINHOS das areas que ja existiam continuam os mesmos. A decisao do
 * cliente foi sobre a POSICAO e o NOME no menu; trocar a URL quebraria todo
 * link ja compartilhado sem que ninguem tivesse pedido isso. Por isso
 * "Monitoramento e Logs" mora em `/operacoes` e "Pausas e Escalacao" tem
 * `/atencao` como filha: o prefixo deixou de significar hierarquia de menu e
 * passou a ser so o lugar onde a tela mora.
 *
 * ## Nenhuma tela ficou orfa
 *
 * A lista entregue e mais curta do que o conjunto de telas que ja existe. Tela
 * com rota e sem nada que a chame nao esta entregue, entao toda tela que perdeu
 * a entrada de primeiro nivel virou SUB-AREA da area que a contem. Um teste
 * varre as rotas do roteador exigindo link alcancavel para cada uma.
 *
 * O tipo `Scope` vem do pacote compartilhado de proposito: escrever o nome do
 * escopo como string livre aqui duplicaria o catalogo, e um erro de digitacao
 * viraria "area sem acesso" silenciosa em vez de erro de compilacao.
 */

/** Fase em que a area passa a ter dado real, quando ja foi decidida. */
export type Fase = 0 | 1 | 2 | 3 | 4 | 5;

export type NavChild = {
  readonly id: string;
  readonly label: string;
  readonly path: string;
  /** Sub-area sem tela propria ainda. Ausente significa implementada. */
  readonly implemented?: boolean;
  readonly availableFromPhase?: Fase;
  readonly pendingReason?: string;
};

export type NavArea = {
  /** Identificador estavel (contrato de codigo). */
  readonly id: string;
  /** Rotulo em portugues, como o cliente escreveu na barra lateral entregue. */
  readonly label: string;
  readonly path: string;
  /** Escopo minimo para ver a area. Servidor decide de novo; isto e so a UI. */
  readonly requiredScope: Scope;
  /**
   * A area JA tem tela propria.
   *
   * Este campo e a FONTE UNICA de "esta pronta": a barra lateral usa para
   * decidir se mostra o selo de pendencia, e o roteador usa para decidir se
   * serve a tela de pendencia. Duas listas separadas divergiriam, e a
   * divergencia seria silenciosa: a area ganharia tela e continuaria dizendo
   * "sem dado", ou pior, a rota de pendencia venceria e a tela nova ficaria
   * inalcancavel.
   */
  readonly implemented: boolean;
  /**
   * Fase em que a area passa a ter dado real.
   *
   * OPCIONAL de proposito. As areas que entraram com o layout de 17/09
   * (Ativos e Conhecimento, Usuarios e Acesso) ainda nao tiveram fase
   * decidida, e escrever um numero aqui para preencher o campo seria inventar
   * um compromisso que ninguem assumiu. Sem fase, a tela diz que a area foi
   * desenhada e ainda nao tem fonte de dado, que e a verdade.
   */
  readonly availableFromPhase?: Fase;
  /** O que falta para a area ter conteudo. Aparece na tela. */
  readonly pendingReason?: string;
  /**
   * O que a area VAI mostrar quando existir.
   *
   * Sai dos paineis que o cliente desenhou no mockup para aquela area. Sem
   * isso, a tela de pendencia diz o que falta e nao diz o que vem, e quem abre
   * nao consegue conferir se o que foi desenhado e o que ele espera.
   */
  readonly willShow?: readonly string[];
  readonly children?: readonly NavChild[];
};

export const NAV_AREAS: readonly NavArea[] = [
  {
    id: 'overview',
    implemented: true,
    label: 'Visão Geral',
    path: '/',
    requiredScope: 'dashboard:read',
    availableFromPhase: 0,
    children: [{ id: 'executive', label: 'Painel executivo', path: '/executivo' }],
  },
  {
    /*
      Master Switch e a 2a area do layout entregue, e ate 17/09 nao existia
      como tela: o botao de parada morava so no cabecalho. O handoff e
      explicito sobre o que ela precisa carregar ("Pausa deve ser facil.
      Retomar exige Safe Resume. Nunca executar backlog acumulado cegamente").
    */
    id: 'master_switch',
    implemented: true,
    label: 'Master Switch',
    path: '/master-switch',
    requiredScope: 'dashboard:read',
    availableFromPhase: 0,
  },
  {
    id: 'integrations',
    implemented: true,
    label: 'Integrações',
    path: '/integracoes',
    requiredScope: 'dashboard:read',
    availableFromPhase: 0,
  },
  {
    id: 'publishing',
    implemented: true,
    label: 'Publicação',
    path: '/conteudo',
    requiredScope: 'content:read',
    availableFromPhase: 1,
    children: [
      { id: 'portfolio', label: 'Portfólio', path: '/conteudo/portfolio' },
      { id: 'workflow', label: 'Quadro de workflow', path: '/conteudo/workflow' },
      { id: 'loyalty', label: 'Loyalty Cast', path: '/loyalty' },
    ],
  },
  {
    /*
      Audience & Lists e area de primeiro nivel desde 14/09 por decisao do
      cliente (item 1.2: TOP_LEVEL_MODULE), e o layout de 17/09 confirma a
      posicao. "Audiencia e crescimento" (o agregado) passou a ser filha:
      era area irma, e duas entradas comecando com "Audiencia" no mesmo nivel
      obrigavam a pessoa a adivinhar qual das duas abre listas.
    */
    id: 'audience_lists',
    implemented: true,
    label: 'Audience & Lists',
    path: '/audiencia/listas',
    requiredScope: 'audience:segment:read',
    availableFromPhase: 3,
    children: [
      { id: 'audience_import', label: 'Importar por arquivo', path: '/audiencia/importacao' },
      { id: 'audience_growth', label: 'Audiência e crescimento', path: '/audiencia' },
    ],
  },
  {
    id: 'community',
    implemented: true,
    label: 'Comunidade e Moderação',
    path: '/comunidade',
    requiredScope: 'community:aggregate:read',
    availableFromPhase: 4,
  },
  {
    id: 'commercial',
    implemented: true,
    label: 'Vendas e Agendamento',
    path: '/comercial',
    requiredScope: 'commercial:aggregate:read',
    availableFromPhase: 4,
  },
  {
    /*
      "Aquisicao e custos" ate 17/09. O layout entregue chama de Midia Paga, e o
      rotulo do cliente manda no rotulo da tela. O caminho continua `/aquisicao`
      para nao quebrar link.

      Experimentos pagos vem junto: eram filhos de Aprendizado, e o layout nao
      tem area de Aprendizado de primeiro nivel. Experimento pago e leitura de
      midia paga, entao e aqui que quem opera vai procurar.
    */
    id: 'paid_media',
    implemented: true,
    label: 'Mídia Paga',
    path: '/aquisicao',
    requiredScope: 'dashboard:read',
    availableFromPhase: 3,
    children: [
      { id: 'paid_experiments', label: 'Experimentos pagos', path: '/aprendizado/experimentos' },
    ],
  },
  {
    id: 'agenda',
    implemented: false,
    label: 'Agenda Estratégica',
    path: '/agendas',
    requiredScope: 'agenda:read',
    availableFromPhase: 1,
    pendingReason: 'Agenda só nasce de instrução verificada do dono. Nenhuma foi registrada ainda.',
  },
  {
    /*
      O layout nomeia a area pelo que ela E (o gemeo digital), e nao pela fila
      que a alimenta. A tela por tras continua sendo a fila de render, que e o
      que existe hoje de HeyGen no produto.
    */
    id: 'digital_twin',
    implemented: true,
    label: 'Digital Twin (HeyGen)',
    path: '/renders',
    requiredScope: 'dashboard:read',
    availableFromPhase: 2,
  },
  {
    id: 'assets',
    implemented: false,
    label: 'Ativos e Conhecimento',
    path: '/ativos',
    requiredScope: 'content:read',
    pendingReason:
      'A área foi desenhada no layout de 17/09 e ainda não tem fonte de dado decidida. O screen map pede fotos, vídeos, logos, brand guides e evidência comercial vindos do Google Drive; a integração de Drive existe como capacidade, mas nenhum catálogo de ativos é lido por enquanto.',
    // Os seis paineis que o mockup desenha para esta area.
    willShow: [
      'Fotos do Sergio',
      'Logos e brand guides',
      'Loyalty Cast',
      'Evidência comercial',
      'Referências visuais',
      'Indexação do acervo',
    ],
    // Sem `availableFromPhase`: nenhuma fase foi decidida para ela, e escrever
    // um numero aqui seria inventar um compromisso.
  },
  {
    id: 'policies',
    implemented: false,
    label: 'Policies e Aprovações',
    path: '/governanca',
    requiredScope: 'governance:read',
    availableFromPhase: 1,
    pendingReason:
      'Os cinco guardiões e os seis gates já estão registrados; as revisões aparecem quando o conteúdo circular (fase 1).',
    /*
      As tres filhas declaram que estao pendentes, e isso NAO e cosmetica.

      Ate 17/09 elas apareciam como link no menu e nenhuma tinha rota: a
      varredura que gera as telas de pendencia so olhava areas de primeiro
      nivel, entao os tres links caiam no `path="*"`, ou seja na tela de
      "pagina nao encontrada". O defeito era antigo e silencioso, porque um
      404 no fim do roteador nao levanta erro nenhum.

      Quem achou foi o guard de alcancabilidade escrito junto com esta
      reorganizacao. Declarar a pendencia da a cada uma a mesma tela honesta do
      pai, que diz o que vai aparecer ali e por que ainda nao aparece.
    */
    children: [
      {
        id: 'reviews',
        label: 'Revisões e atrito',
        path: '/governanca/revisoes',
        implemented: false,
        availableFromPhase: 1,
        pendingReason:
          'As revisões dos cinco guardiões aparecem quando o conteúdo começar a circular (fase 1).',
      },
      {
        id: 'escalations',
        label: 'Escalações',
        path: '/governanca/escalacoes',
        implemented: false,
        availableFromPhase: 1,
        pendingReason:
          'A escalação nasce de um gate que reprovou duas vezes. Nenhuma foi registrada ainda; as que existirem aparecem também na caixa de atenção.',
      },
      {
        id: 'exceptions',
        label: 'Exceções',
        path: '/governanca/excecoes',
        implemented: false,
        availableFromPhase: 1,
        pendingReason:
          'Exceção é permissão temporária concedida por quem responde pelo gate, e é registrada com prazo. Nenhuma foi concedida ainda.',
      },
    ],
  },
  {
    /*
      Esta area JA existia, com outro nome: era a "Caixa de atencao".

      Nao criei tela nova, e a razao e medida: a caixa de atencao lista
      exatamente tres tipos de item, `EscalationRecord`, `PauseState` e
      `HoldState`, ou seja escalacao, pausa ativa e hold. O rotulo que o cliente
      desenhou descreve esse conteudo melhor do que o nosso descrevia. Construir
      um `/pausas` ao lado duplicaria a mesma leitura em duas telas, e a copia
      que envelhece e sempre a menos usada.

      O caminho continua `/atencao` para nao quebrar link ja compartilhado, e o
      cabecalho segue apontando para ca (§6 exige acesso persistente).
    */
    id: 'pauses',
    implemented: true,
    label: 'Pausas e Escalação',
    path: '/atencao',
    requiredScope: 'dashboard:read',
    availableFromPhase: 1,
  },
  {
    id: 'users',
    implemented: false,
    label: 'Usuários e Acesso',
    path: '/usuarios',
    requiredScope: 'configuration:read',
    pendingReason:
      'A área foi desenhada no layout de 17/09. O catálogo de papéis e escopos já existe no servidor e é ele que decide o acesso hoje; o que falta é a tela de administrar convite, papel e step-up, e ela não pode ser inventada antes da decisão sobre SSO do Google Workspace e MFA.',
    willShow: ['Google Workspace SSO', 'Reautenticação (step-up) por ação privilegiada'],
  },
  {
    /*
      Era "Operacoes e integracoes". Integracoes virou area propria no layout
      (a 3a), entao o que sobra aqui e a observacao do sistema: filas, motor,
      aprendizado e o rastro de auditoria.
    */
    id: 'monitoring',
    implemented: true,
    label: 'Monitoramento e Logs',
    path: '/operacoes',
    requiredScope: 'dashboard:read',
    availableFromPhase: 0,
    children: [
      { id: 'motor', label: 'Motor', path: '/motor' },
      { id: 'learning', label: 'Aprendizado e recomendações', path: '/aprendizado' },
      {
        id: 'audit',
        label: 'Auditoria e rastreio',
        path: '/auditoria',
        implemented: false,
        availableFromPhase: 1,
        pendingReason:
          'O log append-only já existe e já recebe eventos; o explorador de rastreio entra na fase 1.',
      },
    ],
  },
  {
    /*
      As tres primeiras filhas sao a resposta do Ferrari de 17/09 sobre
      complementar as 16 areas do layout com o que a Console SPEC §12 tem a
      mais: _"o que tiver a mais, bota a mais ai pra gente ter mais
      informacao"_.

      Medido antes de construir: das 18 da §12, 14 ja tinham lugar nas 16. As
      que faltavam eram "Tetos & Limites", "Autonomia" e "History & Versions",
      e nenhuma delas e dado novo. As duas primeiras ja estavam NESTA tela,
      misturadas aos demais parametros; o que faltava era caminho ate elas.

      Elas entram como SUB-AREA, e nao como area de primeiro nivel, porque a
      barra lateral que o cliente desenhou tem 16 itens e um ritmo visual; 19
      ou 20 no primeiro nivel obrigam a rolar num notebook. Sub-area aparece na
      barra do mesmo jeito, entao nada fica escondido, que era o objetivo dele.
    */
    id: 'advanced_settings',
    implemented: true,
    label: 'Configurações Avançadas',
    path: '/controles',
    requiredScope: 'configuration:read',
    availableFromPhase: 0,
    children: [
      { id: 'hard_limits', label: 'Tetos e limites', path: '/controles/tetos-e-limites' },
      { id: 'autonomy', label: 'Autonomia', path: '/controles/autonomia' },
      {
        /*
          A unica das tres que e trabalho de verdade, e nao um caminho.

          §39 (Version History) e §40 (Rollback) estao especificados, e o dado
          existe no banco: toda mudanca de configuracao ja nasce versionada e
          auditada. O que nao existe e a tela de navegar por esse historico e
          voltar uma versao. Nascer dizendo "sem dado" e a verdade; fingir que
          a area esta pronta seria pior do que nao ter a entrada.
        */
        id: 'version_history',
        label: 'Histórico e versões',
        path: '/controles/historico',
        implemented: false,
        pendingReason:
          'Toda mudança de configuração já nasce versionada e auditada, e o rollback existe no modelo de dados (Console SPEC §39 e §40). O que falta é a tela de percorrer esse histórico e voltar uma versão, e ela não foi construída ainda.',
      },
      { id: 'data_subject', label: 'Pedidos do titular', path: '/titular' },
    ],
  },
];

/**
 * O item ancorado no RODAPE da barra lateral, separado das 16 areas.
 *
 * Ele e uma lista propria, e nao a 17a entrada de `NAV_AREAS`, porque o layout
 * o desenha destacado do resto: fica colado embaixo, depois de um vao. Mistura-
 * lo com as areas faria a contagem de 16 deixar de bater com o desenho, e e
 * essa contagem que o teste afirma.
 */
export const NAV_RODAPE: readonly NavArea[] = [
  {
    id: 'help',
    implemented: false,
    label: 'Ajuda e Documentação',
    path: '/ajuda',
    requiredScope: 'dashboard:read',
    pendingReason:
      'A área foi desenhada no layout de 17/09. O material que ela deve servir (as políticas aprovadas, o guia de operação e o histórico de decisões) existe no repositório como documento, e ainda não foi decidido o que dele é publicado para quem opera.',
  },
];

/** Todas as entradas navegáveis, incluindo o rodapé e as sub-áreas. */
export const TODAS_AS_ENTRADAS: readonly NavChild[] = [...NAV_AREAS, ...NAV_RODAPE].flatMap(
  (area) => [
    { id: area.id, label: area.label, path: area.path, implemented: area.implemented },
    ...(area.children ?? []),
  ],
);

/**
 * Estado de PAUSA, derivado do banco (nunca de toggle de front-end).
 *
 * Ele responde uma pergunta so: alguem apertou pausa? Nao confundir com o
 * estado OPERACIONAL abaixo, que e o que a pessoa le no cabecalho.
 *
 * Nao existe rotulo para ele de proposito: quando existia, alguem o mostrava na
 * tela, e foi assim que "sem pausa" virou "em operacao". O unico consumidor
 * deste eixo e o botao de parar e retomar.
 */
export type SystemState = 'running' | 'partially_paused' | 'paused' | 'degraded';

/**
 * O estado que a pessoa LE, e que o servidor deriva de pausa MAIS interruptor
 * de efeito externo.
 *
 * Ate 17/09 o Console mostrava "Sistema em operacao" porque `system_state`
 * dizia `running`, e `running` ali significa apenas "ninguem apertou pausa".
 * O ambiente estava (e esta) em TESTE: nenhum interruptor ligado, nenhuma
 * capacidade validada, e as portas de modelo, render, publicacao e metrica em
 * duble. O dono apontou: dizer "em operacao" ali e mentira.
 */
export type OperationalState = 'paused' | 'partially_paused' | 'no_external_effect' | 'running';

export const OPERATIONAL_STATE_LABEL: Record<OperationalState, string> = {
  paused: 'parado',
  partially_paused: 'parcialmente parado',
  no_external_effect: 'em teste, sem efeito externo',
  running: 'em operação',
};

/** A frase que explica o rotulo. O rotulo sozinho ainda deixa duvida. */
export const OPERATIONAL_STATE_EXPLICACAO: Record<OperationalState, string> = {
  paused: 'Alguém parou o sistema. Nenhum efeito externo novo acontece.',
  partially_paused: 'Uma ou mais categorias estão paradas; o resto segue como está configurado.',
  no_external_effect:
    'Nada publica, gasta ou envia: todos os interruptores de efeito externo estão desligados. O que roda aqui são dublês.',
  running: 'Pelo menos um canal pode produzir efeito externo agora.',
};

/**
 * Cor do ponto de status no cabeçalho.
 *
 * Verde e reservado para operar de verdade. "Em teste" e azul, e nao verde:
 * verde ali foi exatamente o que fez a tela parecer que o sistema estava no ar.
 * O ponto acompanha o texto, nunca o substitui.
 */
export const OPERATIONAL_STATE_DOT: Record<OperationalState, string> = {
  paused: 'bg-danger',
  partially_paused: 'bg-warn',
  no_external_effect: 'bg-accent',
  running: 'bg-ok',
};

export const ENVIRONMENT_LABEL: Record<string, string> = {
  development: 'DESENVOLVIMENTO',
  sandbox: 'SANDBOX',
  staging: 'TESTE',
  production: 'PRODUÇÃO',
};
